import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Paper,
  Grid,
  Chip,
  Divider,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PersonIcon from "@mui/icons-material/Person";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PaidIcon from "@mui/icons-material/Paid";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";

// Import Timeline components
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { auth, db } from "../../firebase";

const getActivityIcon = (type) => {
  switch (type) {
    case "receipt":
      return <ReceiptLongIcon />;
    case "invoice":
      return <AttachMoneyIcon />;
    case "payroll":
      return <PaidIcon />;
    case "client":
      return <PersonIcon />;
    case "project":
      return <FolderOpenIcon />;
    default:
      return <PersonIcon />;
  }
};

const getActivityColor = (type) => {
  switch (type) {
    case "receipt":
      return "primary";
    case "invoice":
      return "success";
    case "payroll":
      return "error";
    case "client":
      return "info";
    case "project":
      return "warning";
    default:
      return "grey";
  }
};

export default function AdminUserDetails() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState({
    receipts: 0,
    invoices: 0,
    projects: 0,
    clients: 0,
    payroll: 0,
  });

  const adminEmail = "devabyltd@gmail.com";

  useEffect(() => {
    // Basic Security Redirect Check
    if (!auth.currentUser || auth.currentUser.email !== adminEmail) {
      navigate("/");
      return;
    }

    const fetchUserDetails = async () => {
      try {
        setLoading(true);
        // 1. Fetch Key User Profile Doc
        const userRef = doc(db, "users", uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          setUserData(userSnap.data());
        }

        // 2. Fetch all related collections sequentially (or in parallel)
        const timelineEvents = [];
        const newStats = {
          receipts: 0,
          invoices: 0,
          projects: 0,
          clients: 0,
          payroll: 0,
        };

        // Subcollections/Collections queries
        const fetchCollectionForUser = async (
          collName,
          type,
          detailsMapper,
        ) => {
          let docsQuery;
          // Most collections use 'uid' as field except users/{uid}/clients
          if (collName === "clients") {
            docsQuery = query(collection(db, `users/${uid}/clients`));
          } else if (
            collName === "receipts" ||
            collName === "projects" ||
            collName === "payroll"
          ) {
            docsQuery = query(
              collection(db, collName),
              where("uid", "==", uid),
            );
          } else if (collName === "invoices") {
            docsQuery = query(
              collection(db, collName),
              where("userId", "==", uid),
            );
          }

          const snap = await getDocs(docsQuery);

          snap.forEach((docSnap) => {
            const data = docSnap.data();
            newStats[type]++;

            // Try to figure out creation timestamp
            const createdAt =
              data.createdAt || data.date || data.issueDate || data.scannedAt;

            if (createdAt) {
              timelineEvents.push({
                id: docSnap.id,
                type,
                timestamp: createdAt?.toDate
                  ? createdAt.toDate()
                  : new Date(createdAt),
                title: `${type.charAt(0).toUpperCase() + type.slice(1)} Created`,
                details: detailsMapper(data),
              });
            }
          });
        };

        await Promise.all([
          fetchCollectionForUser(
            "receipts",
            "receipt",
            (d) => `Amount: ${d.amount} | Vendor: ${d.vendor || "Unknown"}`,
          ),
          fetchCollectionForUser(
            "invoices",
            "invoice",
            (d) =>
              `Amount: ${d.totalAmount} | To: ${d.clientName || "Unknown"} | Status: ${d.status}`,
          ),
          fetchCollectionForUser(
            "projects",
            "project",
            (d) => `Name: ${d.name || "Unnamed"}`,
          ),
          fetchCollectionForUser(
            "payroll",
            "payroll",
            (d) =>
              `Amount: ${d.amount} | Payee: ${d.employeeName || "Unknown"}`,
          ),
          fetchCollectionForUser(
            "clients",
            "client",
            (d) => `Client ID generated (Data Encrypted)`,
          ), // clients are encrypted so name won't be easily readable here without key
        ]);

        // Add User Signup Event
        if (userSnap.exists() && userSnap.data().createdAt) {
          timelineEvents.push({
            id: "signup",
            type: "signup",
            timestamp: userSnap.data().createdAt.toDate(),
            title: "User Signed Up",
            details: `Email: ${userSnap.data().email}`,
          });
        }

        // Sort descending by timestamp
        timelineEvents.sort((a, b) => b.timestamp - a.timestamp);

        setActivities(timelineEvents);
        setStats(newStats);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching user data for admin view:", err);
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [uid, navigate]);

  if (!auth.currentUser || auth.currentUser.email !== adminEmail) {
    return null; // Will redirect in useEffect
  }

  if (loading) {
    return (
      <Container sx={{ mt: 8, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!userData) {
    return (
      <Container sx={{ mt: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin")}
          sx={{ mb: 2 }}
        >
          Back to Admin
        </Button>
        <Typography variant="h5">User not found</Typography>
      </Container>
    );
  }

  const getTrialEnds = () => {
    if (!userData.trialStartedAt) return "—";
    const d = new Date(
      userData.trialStartedAt.toDate().getTime() + 14 * 24 * 60 * 60 * 1000,
    );
    return d.toLocaleDateString();
  };

  return (
    <Container sx={{ my: 6, maxWidth: 900 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin")}
          sx={{ mr: 2 }}
          variant="outlined"
          color="inherit"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            User Profile: {userData.email}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            UID: {uid}
          </Typography>
        </Box>
      </Box>

      {/* Info Card */}
      <Paper elevation={2} sx={{ p: 4, borderRadius: 3, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              STATUS
            </Typography>
            <Box mt={0.5} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {(userData.plan === "pro" || userData.plan === "pro_plus") ? (
                <Chip label={userData.plan === "pro_plus" ? "Pro Plus" : "Pro Plan"} color="success" />
              ) : (
                <Chip label="Free / Trial" color="warning" />
              )}
              {userData.subscriptionStatus === "past_due" && (
                <Chip label="⚠️ Payment Failed" color="error" size="small" />
              )}
              {userData.subscriptionStatus === "canceled" && (
                <Chip label="Cancelled" color="default" size="small" />
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              TRIAL ENDS
            </Typography>
            <Typography variant="body1" fontWeight={500} mt={0.5}>
              {getTrialEnds()}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              JOINED
            </Typography>
            <Typography variant="body1" fontWeight={500} mt={0.5}>
              {userData.createdAt?.toDate?.().toLocaleDateString() || "—"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              ROLE
            </Typography>
            <Typography
              variant="body1"
              fontWeight={500}
              mt={0.5}
              sx={{ textTransform: "capitalize" }}
            >
              {userData.role || "Freelancer"}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Stats Summary */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        Application Usage Overview
      </Typography>
      <Grid container spacing={2} sx={{ mb: 5 }}>
        <Grid item xs={6} sm={4} md={2.4}>
          <Paper
            sx={{
              p: 2,
              textAlign: "center",
              borderTop: "4px solid #1976d2",
              borderRadius: 2,
            }}
          >
            <Typography variant="h4" fontWeight={700} color="primary">
              {stats.receipts}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              RECEIPTS
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Paper
            sx={{
              p: 2,
              textAlign: "center",
              borderTop: "4px solid #2e7d32",
              borderRadius: 2,
            }}
          >
            <Typography variant="h4" fontWeight={700} color="success.main">
              {stats.invoices}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              INVOICES
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Paper
            sx={{
              p: 2,
              textAlign: "center",
              borderTop: "4px solid #0288d1",
              borderRadius: 2,
            }}
          >
            <Typography variant="h4" fontWeight={700} color="info.main">
              {stats.clients}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              CLIENTS
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Paper
            sx={{
              p: 2,
              textAlign: "center",
              borderTop: "4px solid #ed6c02",
              borderRadius: 2,
            }}
          >
            <Typography variant="h4" fontWeight={700} color="warning.main">
              {stats.projects}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              PROJECTS
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Paper
            sx={{
              p: 2,
              textAlign: "center",
              borderTop: "4px solid #d32f2f",
              borderRadius: 2,
            }}
          >
            <Typography variant="h4" fontWeight={700} color="error.main">
              {stats.payroll}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              PAYROLL
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        Activity Timeline
      </Typography>

      <Paper
        elevation={0}
        sx={{ p: 3, border: "1px solid #e5e7eb", borderRadius: 3 }}
      >
        {activities.length === 0 ? (
          <Typography color="text.secondary" align="center" py={4}>
            No activity found for this user.
          </Typography>
        ) : (
          <Timeline position="right" sx={{ p: 0 }}>
            {activities.map((activity, index) => {
              const isLast = index === activities.length - 1;
              return (
                <TimelineItem key={activity.id + index}>
                  <TimelineOppositeContent
                    sx={{ m: "auto 0", flex: 0.25, pl: 0 }}
                    align="right"
                    variant="body2"
                    color="text.secondary"
                  >
                    {activity.timestamp.toLocaleDateString()}
                    <br />
                    <Typography variant="caption">
                      {activity.timestamp.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </TimelineOppositeContent>

                  <TimelineSeparator>
                    <TimelineDot
                      color={getActivityColor(activity.type)}
                      sx={{ boxShadow: "0 0 0 3px rgba(0,0,0,0.03)" }}
                    >
                      {getActivityIcon(activity.type)}
                    </TimelineDot>
                    {!isLast && <TimelineConnector />}
                  </TimelineSeparator>

                  <TimelineContent sx={{ py: "16px", px: 2 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      component="span"
                    >
                      {activity.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {activity.details}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        )}
      </Paper>
    </Container>
  );
}
